import * as zod from 'zod';

import { Step, ActionRequiredError } from '@sb/remote-control/types';
import { buildConditional } from '@sb/remote-control/util/conditionalsBuilder';

import { validateConditional } from '../util/conditionalsValidator';

export namespace AntigravityStep {
  export const name = 'Antigravity';
  export const description =
    'Pause routine and allow antigravity positioning. Resume when done.';
  export const librarySection = Step.LibrarySection.InterfaceWithOperators;
  export const featureFlag = 'antigravityStep' as const;

  export const argumentKind = 'Antigravity';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    message: zod.string().optional(),
    condition: zod.string().nullable().default(null),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const condition = args.condition
      ? buildConditional(JSON.parse(args.condition))
      : null;

    return {
      ...stepData,
      stepKind: 'Antigravity',
      args: {
        message: args.message,
        condition,
      },
    };
  };

  export const validator: Step.Validator = ({ stepConfiguration, routine }) => {
    const args = (stepConfiguration?.args || {}) as Arguments;

    if (args.condition == null) return;
    const conditions = JSON.parse(args?.condition || '[]');

    // Validate the conditional
    validateConditional(conditions, routine);

    // Validate that only IO conditions are used
    for (const orGroup of conditions) {
      for (const condition of orGroup) {
        if (condition.variable && condition.variable.kind !== 'ioState') {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message:
              'Only IO conditions are allowed for the alternative end condition.',
          });
        }
      }
    }
  };
}

AntigravityStep satisfies Step.StepKindInfo;
